import { useState, type CSSProperties, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import { brand, brandAppName } from '../brand';

/**
 * 職員登入表（**桌面後台專用**；產品負責人 2026-10-06 拍板）
 *
 * 為何同會員登入分開：會員登入頁（手機）**只准電話**（免密碼）；
 *   職員（`ai85`／`staff`）只有 email ＋ 密碼 → 兩者欄位完全唔同，混埋一齊會又亂又易錯。
 * ⚠️ 只喺 `/admin` 未登入時顯示（唔喺會員登入頁宣傳）。
 */
const inputStyle: CSSProperties = {
  width: '100%',
  borderRadius: 8,
  padding: '8px 12px',
  border: '1px solid var(--color-divider)',
  background: 'var(--color-bg)',
  color: 'var(--color-text)',
};

export default function StaffLoginForm() {
  const { t, i18n } = useTranslation();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    const r = await login(email.trim(), password);
    setBusy(false);
    /* 成功：AuthContext 更新 → `AdminLayout` 自動顯示後台 */
    if (!r.ok) setError(r.error || t('login_failed'));
  }

  return (
    <form
      onSubmit={onSubmit}
      className="max-w-lg w-full rounded-xl px-8 pb-8 pt-10"
      style={{ background: 'var(--color-card)', boxShadow: 'var(--shadow-soft)' }}
    >
      <img
        src={brand.logos.horizontal}
        alt={brandAppName(i18n.language)}
        style={{ width: '100%', maxWidth: 300, height: 'auto', display: 'block', margin: '0 auto 48px' }}
      />
      <label className="block text-sm mb-1" htmlFor="staff-email" style={{ color: 'var(--color-text-secondary)' }}>
        {t('login_email')}
      </label>
      <input
        id="staff-email"
        type="email"
        required
        autoComplete="username"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="mb-3"
        style={inputStyle}
      />
      <label className="block text-sm mb-1" htmlFor="staff-password" style={{ color: 'var(--color-text-secondary)' }}>
        {t('login_password')}
      </label>
      <input
        id="staff-password"
        type="password"
        required
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        className="mb-4"
        style={inputStyle}
      />

      {error && (
        <p className="text-sm mb-3" style={{ color: 'var(--color-danger)' }}>
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={busy}
        className="w-full rounded-lg px-4 py-2 font-semibold"
        style={{ background: 'var(--color-primary)', color: 'var(--color-on-primary)', opacity: busy ? 0.6 : 1 }}
      >
        {busy ? t('login_working') : t('login_submit')}
      </button>
    </form>
  );
}
